import * as sections from '@services/sections/lib/onboarding'
import { l3OnboardingApplicationTokenHolders } from '@services/sections/lib/onboarding'
import { L3OnboardingFlowGroup } from '@shared/graphql'

import type { CopRoutes } from '@circlefin/cop-ui/hooks/l3-onboarding/groups/utils'

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export const isValidOperationId = (value: string): boolean =>
  UUID_REGEX.test(value)

/**
 * Replace [operationId] placeholder in a route with the actual operation ID.
 */
export const replaceOperationIdInRoute = (
  route: string,
  operationId: string,
): string => route.replace('[operationId]', operationId)

/**
 * Map group names to UI sections routes.
 */
export const createCopRoutes = ({
  operationId,
}: {
  operationId: string
}): CopRoutes => ({
  // Business details
  [L3OnboardingFlowGroup.businessRegistrationDetails]: {
    translationKey: 'businessRegistrationDetails',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationBusinessRegistration.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.basicBusinessInfo]: {
    translationKey: 'basicBusinessInfo',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationBusinessInformation.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.physicalBusinessAddress]: {
    translationKey: 'physicalBusinessAddress',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationPhysicalAddress.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.registeredOfficeAddress]: {
    translationKey: 'registeredOfficeAddress',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationRegisteredAddress.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.intendedUseOfAccount]: {
    translationKey: 'intendedUseOfAccount',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationIntendedUse.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.entityOperations]: {
    translationKey: 'entityOperations',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationEntityOperations.route,
      operationId,
    ),
  },

  // Related Parties
  [L3OnboardingFlowGroup.beneficialOwners]: {
    translationKey: 'beneficialOwners',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationBeneficialOwners.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.tokenHolders]: {
    translationKey: 'tokenHolders',
    route: replaceOperationIdInRoute(
      l3OnboardingApplicationTokenHolders.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.individualsWithSignificantControl]: {
    translationKey: 'individualsWithSignificantControl',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationLegalControl.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.trustRelatedParties]: {
    translationKey: 'trustRelatedParties',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationRelatedPartiesToTrust.route,
      operationId,
    ),
  },

  // Investors
  [L3OnboardingFlowGroup.investors]: {
    translationKey: 'investors',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationInvestors.route,
      operationId,
    ),
  },

  // Top 5 investors
  [L3OnboardingFlowGroup.topFiveInvestors]: {
    translationKey: 'investors',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationTopFiveInvestors.route,
      operationId,
    ),
  },

  // Donors
  [L3OnboardingFlowGroup.donors]: {
    translationKey: 'donors',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationDonors.route,
      operationId,
    ),
  },

  // Significant donors above 25 percent
  [L3OnboardingFlowGroup.significantDonorsAbove25Pct]: {
    translationKey: 'donors',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationDonorsAbove25Pct.route,
      operationId,
    ),
  },

  // Board members
  [L3OnboardingFlowGroup.boardMembers]: {
    translationKey: 'boardMembers',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationBoardMembers.route,
      operationId,
    ),
  },

  // Asset Manager
  [L3OnboardingFlowGroup.assetManager]: {
    translationKey: 'assetManager',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationAssetManager.route,
      operationId,
    ),
  },

  [L3OnboardingFlowGroup.partyPerformingInvestorDueDiligence]: {
    translationKey: 'partyPerformingInvestorDueDiligence',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationPartyPerformingInvestorDueDiligence.route,
      operationId,
    ),
  },

  // Additional Business information
  [L3OnboardingFlowGroup.licensingAndRegistration]: {
    translationKey: 'licensingAndRegistration',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationLicensing.route,
      operationId,
    ),
  },
  [L3OnboardingFlowGroup.financialCrimesCompliance]: {
    translationKey: 'financialCrimesCompliance',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationFinancialCrimesInformation.route,
      operationId,
    ),
  },

  // Additional users
  [L3OnboardingFlowGroup.additionalUsers]: {
    translationKey: 'additionalUsers',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationAdditionalUsers.route,
      operationId,
    ),
  },

  // Additional user (KYC)
  [L3OnboardingFlowGroup.additionalUsersPersonsKyc]: {
    translationKey: 'additionalUsersPersonsKyc',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationAdditionalUserKyc.route,
      operationId,
    ),
  },

  // Authorized representative
  [L3OnboardingFlowGroup.authorizedRepresentative]: {
    translationKey: 'authorizedRepresentative',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationAuthorizedRepresentative.route,
      operationId,
    ),
  },

  // Tax info Eea Vat
  [L3OnboardingFlowGroup.taxInfoEeaVat]: {
    translationKey: 'taxInfoEea',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationTaxInfoEeaVat.route,
      operationId,
    ),
  },

  // Fatca Information
  [L3OnboardingFlowGroup.fatcaInformation]: {
    translationKey: 'fatcaInformation',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationFatcaInformation.route,
      operationId,
    ),
  },

  // Self Certification CRS
  [L3OnboardingFlowGroup.selfCertificationCrs]: {
    translationKey: 'selfCertificationCrs',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationSelfCertificationCrs.route,
      operationId,
    ),
  },

  // Certifications
  [L3OnboardingFlowGroup.certificationInfo]: {
    translationKey: 'certificationInfo',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationCertifications.route,
      operationId,
    ),
  },

  // KYC Certifications
  [L3OnboardingFlowGroup.kycCertificationInfo]: {
    translationKey: 'kycCertificationInfo',
    route: replaceOperationIdInRoute(
      sections.l3OnboardingApplicationCertificationsKyc.route,
      operationId,
    ),
  },

  // Overview groups
  [L3OnboardingFlowGroup.businessDetails]: {
    translationKey: 'businessDetails',
  },
  [L3OnboardingFlowGroup.relatedParties]: {
    translationKey: 'relatedParties',
  },
  [L3OnboardingFlowGroup.additionalInformation]: {
    translationKey: 'additionalInformation',
  },
  [L3OnboardingFlowGroup.taxInfoEea]: {
    translationKey: 'taxInfoEea',
  },
  [L3OnboardingFlowGroup.certifications]: {
    translationKey: 'certifications',
  },
  [L3OnboardingFlowGroup.additionalUsersKyc]: {
    translationKey: 'additionalUsersKyc',
  },
  [L3OnboardingFlowGroup.kycCertifications]: {
    translationKey: 'kycCertifications',
  },
})
